import { useContext, useEffect, useState } from "react";
import { cloneDeep } from "lodash";
import { ClientMarketExpansionModel } from "@app/client";
import { ClientContext } from "@app/admin/contexts/ClientContext";
import { BasicState } from "@app/geography";
import GeographyService from "@app/geography/geographyService";
import { NetworkTaxonomyLineOfBusinessSimple } from "@app/network-taxonomies/networkTaxonomy";
import NetworkTaxonomyService from "@app/network-taxonomies/networkTaxonomyService";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { CheckboxStateEnum, QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { MultiselectItem, MultiselectOnChangeResponse, QESMultiSelect } from "@app/shared/qes-standards/forms/Multiselect";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";

interface ClientMarketExpansionProps {
  disabled?: boolean;
}

const ClientMarketExpansion: React.FC<ClientMarketExpansionProps> = ({ disabled = false }) => {
  const [availableStates, setAvailableStates] = useState<BasicState[]>([]);
  const [lineOfBusinessOptions, setLineOfBusinessOptions] = useState<NetworkTaxonomyLineOfBusinessSimple[]>([]);
  const [linesOfBusinessLoading, setLinesOfBusinessLoading] = useState<boolean>(true);
  const [selectAllChecked, setSelectAllChecked] = useState<boolean>(false);
  const [selectedLineOfBusiness, setSelectedLineOfBusiness] = useState<number>(null);
  const [stateOptions, setStateOptions] = useState<{ [key: string]: MultiselectItem }>(null);
  const [statesLoading, setStatesLoading] = useState<boolean>(true);

  const clientContext = useContext(ClientContext);

  const geographyService = new GeographyService();
  const networkTaxonomyService = new NetworkTaxonomyService();

  useEffect(() => {
    getLinesOfBusiness();

    getStates();
  }, []);

  useEffect(() => {
    const newStateOptions = cloneDeep(stateOptions);

    if (!!selectedLineOfBusiness) {
      const clientMarketExpansion: ClientMarketExpansionModel = clientContext.clientMarketExpansion.find(x => x.lineOfBusinessId === selectedLineOfBusiness);
      const selectedStateAbbreviations = clientMarketExpansion.stateAbbreviations || [];

      const newStateNames = selectedStateAbbreviations.map((stateAbbreviation) => getStateName(stateAbbreviation)).filter(Boolean);

      for (const x of Object.keys(newStateOptions)) {
        if (newStateNames.includes(x)) {
          newStateOptions[x].currentState = CheckboxStateEnum.Checked;
        } else {
          newStateOptions[x].currentState = CheckboxStateEnum.Unchecked;
        }
      }

      setStateOptions(newStateOptions);
      if (clientMarketExpansion.stateAbbreviations.length === availableStates.length) {
        setSelectAllChecked(true);
      } else {
        setSelectAllChecked(false);
      }
    }

    else {
      // if no lines of business are selected, deselect all states in case any are selected
      // from when there were lines of business selected in order to avoid visual confusion
      if (!!stateOptions) {
        for (const x of Object.keys(newStateOptions)) {
          newStateOptions[x].currentState = CheckboxStateEnum.Unchecked;
        }

        setStateOptions(newStateOptions);
      }
    }
  }, [clientContext.clientMarketExpansion, selectedLineOfBusiness]);

  const getLineOfBusinessName = (lineOfBusinessId: number): string => {
    const lineOfBusiness = lineOfBusinessOptions.find(x => x.lineOfBusinessId === lineOfBusinessId);
    return lineOfBusiness?.name;
  }

  const getLinesOfBusiness = (): void => {
    networkTaxonomyService.getLinesOfBusiness().then((data) => {
      if (data?.length) {

        // Filter to only the lines of business avaliable in the Market Expansion dashboard: Medicare | Marketplace | Medicaid
        const filteredData = data.filter(item => item.name === "Medicare" || item.name == "Marketplace" || item.name == "Medicaid");
        setLineOfBusinessOptions(filteredData);

        setLinesOfBusinessLoading(false);
      }
    });
  }

  const getStateName = (stateAbbreviation: string): string => {
    const matchedStateModel: BasicState = availableStates.find((x) => x.abbreviation === stateAbbreviation);
    return matchedStateModel?.name;
  }

  const getStates = (): void => {
    geographyService.getStatesForDroplist().then((data) => {
      if (data?.length) {
        const states: { [key: string]: MultiselectItem } = {};
        const unwantedStates = ["GU", "VI"];
        data = data.filter(x => !unwantedStates.includes(x.abbreviation));

        data.forEach((r) => Object.assign(states,
          {
            [r.name]: {
              currentState: CheckboxStateEnum.Unchecked,
              displayValue: r.name,
            }
          }
        ));

        setAvailableStates(data);
        setStateOptions(states);
        setStatesLoading(false);
      }
    });
  }

  const onLineOfBusinessChange = (lineOfBusinessId: number): void => {
    const clientMarketExpansion: ClientMarketExpansionModel[] = cloneDeep(clientContext.clientMarketExpansion);
    const storedClientMarketExpansionModel: ClientMarketExpansionModel = clientMarketExpansion.find(x => x.lineOfBusinessId === lineOfBusinessId);
    if (!storedClientMarketExpansionModel) {
      // if a stored client market expansion model for the line of business does not exist, create one
      const newClientMarketExpansionModel: ClientMarketExpansionModel = {
        lineOfBusinessId,
        stateAbbreviations: [],
      };

      clientMarketExpansion.push(newClientMarketExpansionModel);
    }

    clientContext.setClientMarketExpansion(clientMarketExpansion);
    setSelectedLineOfBusiness(lineOfBusinessId);
  }

  const onSelectAllToggle = () => {
    const newSelectAllValue = !selectAllChecked;
    const newStateOptions = cloneDeep(stateOptions);

    for (const x of Object.keys(newStateOptions)) {
      if (newSelectAllValue) {
        newStateOptions[x].currentState = CheckboxStateEnum.Checked;
      } else {
        newStateOptions[x].currentState = CheckboxStateEnum.Unchecked;
      }
    }

    setStateOptions(newStateOptions);
    setSelectAllChecked(newSelectAllValue);
    updateClientMarketExpansionModel(newSelectAllValue ? availableStates : []);
  }

  const updateClientMarketExpansionModel = (newStates: BasicState[]) => {
    const clientMarketExpansionModels: ClientMarketExpansionModel[] = cloneDeep(clientContext.clientMarketExpansion);
    // assign selected states to selected line of business
    const model: ClientMarketExpansionModel = clientMarketExpansionModels.find(x => x.lineOfBusinessId === selectedLineOfBusiness);

    if (!!model) {
      model.stateAbbreviations = cloneDeep(newStates.map(x => x.abbreviation));
    }

    clientContext.setClientMarketExpansion(clientMarketExpansionModels);
  }

  const updateStates = (response: MultiselectOnChangeResponse): void => {
    const selectedStates: string[] = response.checkedKeys;

    //Convert selected states to BasicState
    const newStates = selectedStates.map((state) => {
      return availableStates.find(x => x.name === state);
    }).filter(Boolean).sort((a, b) => a.abbreviation.localeCompare(b.abbreviation));

    //Update clientMarketExpansion model with new states
    updateClientMarketExpansionModel(newStates);

    //Adjust select all checkbox
    const model: ClientMarketExpansionModel = clientContext.clientMarketExpansion.find(x => x.lineOfBusinessId === selectedLineOfBusiness);
    if (model?.stateAbbreviations.length === availableStates.length) {
      setSelectAllChecked(true);
    } else {
      setSelectAllChecked(false);
    }
  };

  return (
    <div>
      <div className="row">
        <div className="col-md-6">
          <div className="row">
            <div className="col-md-6">
              {(!linesOfBusinessLoading && !clientContext.clientMarketExpansionLoading && !!lineOfBusinessOptions.length) ? (
                <QESSelect
                  disabled={!lineOfBusinessOptions?.length}
                  testId="client-market-expansion--line-of-business"
                  label="Authorized Line Of Business"
                  onChange={(e) => onLineOfBusinessChange(parseInt(e.target.value))}
                  value={selectedLineOfBusiness || ""}
                >
                  {lineOfBusinessOptions.map((lineOfBusiness, index) => (
                    <option
                      key={index}
                      value={lineOfBusiness.lineOfBusinessId}
                    >
                      {lineOfBusiness.name}
                    </option>
                  ))}
                </QESSelect>
              ) : (
                <LoadingSpinner />
              )}
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <QESCheckbox
                checked={selectAllChecked}
                containerClassName="mb-0 mt-3"
                disabled={disabled || !selectedLineOfBusiness}
                label="Select All States"
                testId="client-admin__market-expansion--select-all"
                onChange={() => onSelectAllToggle()}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              {(!statesLoading && !clientContext.clientMarketExpansionLoading && !!stateOptions &&
                <QESMultiSelect
                  containerClassName="mb-3 mt-3"
                  disabled={disabled || !selectedLineOfBusiness}
                  label="Authorized States"
                  onChange={updateStates}
                  options={stateOptions}
                  required={true}
                  testId="client-admin__market-expansion--states"
                  />
              )}
            </div>
          </div>
        </div>
        <div className="offset-md-2 col-md-4">
          <label className="label-m mb-0">
            Selected States Overview
          </label>
          {clientContext.clientMarketExpansion.map((group) => (
            <div className="mt-3" key={group.lineOfBusinessId}>
              <label className="label-s mb-0">
                {getLineOfBusinessName(group.lineOfBusinessId)}
              </label>
              <div className="client-admin__market-expansion--state-box">
                {group.stateAbbreviations?.map((stateAbbreviation) => (
                  <div key={stateAbbreviation}>
                    {getStateName(stateAbbreviation)}
                  </div>
                ))}
              </div>
            </div>
          ))
          }
        </div>
      </div>
    </div>
  );
}

export default ClientMarketExpansion;
